/**
 * Collaborate drawer — add co-agents (collaborators) to the conversation,
 * rebuilt to mirror the KC2 "Collaborate" cascade: pick Campaign → Team →
 * Agent, then add. Collaborators are listed below with a remove action.
 * POST / DELETE /conversations/:id/co-agents via actions.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { UserPlus, Users, Building2, UserCog, X } from 'lucide-react'
import { useCampaigns } from '../../../hooks/useCampaigns.js'
import { useTeams } from '../../../hooks/useTeams.js'
import { useAgentUsers } from '../../../hooks/useAgentUsers.js'
import type { ConversationDetailResponse } from '../../../modules/conversations/types.js'
import ActionDrawer from './ActionDrawer.js'
import { GhostButton, PrimaryButton, SelectField, Field, R } from './primitives.js'
import type { UseConversationActionsReturn } from './useConversationActions.js'

type Conversation = ConversationDetailResponse['conversation']

export interface CollaborateDrawerProps {
  open: boolean
  onClose: () => void
  actions: UseConversationActionsReturn
  conversation: Conversation
}

export default function CollaborateDrawer({
  open,
  onClose,
  actions,
  conversation,
}: CollaborateDrawerProps): JSX.Element {
  const { campaigns } = useCampaigns()
  const { teams } = useTeams(true)
  const { users: agentUsers } = useAgentUsers(true)

  const [campaignId, setCampaignId] = useState('')
  const [teamId, setTeamId] = useState('')
  const [selected, setSelected] = useState('')

  // Reset the cascade each time the drawer opens.
  useEffect(() => {
    if (open) { setCampaignId(''); setTeamId(''); setSelected('') }
  }, [open])

  const coAgentIds = conversation.coAgentIds ?? []

  // agentId → display label (login id / name), for the collaborators list.
  const labelFor = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of agentUsers) if (u.agentId) map.set(u.agentId, u.loginid || u.name || 'Agent')
    return map
  }, [agentUsers])

  const campaignOptions = useMemo(() => campaigns.map((c) => ({ value: c.id, label: c.name })), [campaigns])
  const teamOptions = useMemo(
    () => teams.filter((t) => !campaignId || String(t.campaignId ?? '') === campaignId).map((t) => ({ value: t.id, label: t.name })),
    [teams, campaignId],
  )
  // Agents filtered by campaign + team, excluding the assignee and anyone
  // already collaborating (mirrors KC2's share-agent filter).
  const agentOptions = useMemo(
    () => !campaignId ? [] : agentUsers
      .filter((u) => u.agentId
        && (u.campaignIds?.includes(campaignId) || String(u.campaignId ?? '') === campaignId)
        && (!teamId || u.teamIds?.includes(teamId) || String(u.teamId ?? '') === teamId)
        && !coAgentIds.includes(u.agentId)
        && u.agentId !== conversation.assignedAgentId)
      .map((u) => ({ value: u.agentId as string, label: u.loginid || u.name || 'Agent' })),
    [agentUsers, campaignId, teamId, coAgentIds, conversation.assignedAgentId],
  )

  const add = async (): Promise<void> => {
    if (!selected) return
    const ok = await actions.addCoAgent(selected)
    if (ok) setSelected('')
  }

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Collaborate"
      subtitle="Add co-agents to this conversation"
      icon={<UserPlus className="h-4 w-4" />}
      info={{
        lead: 'Brings other agents onto this conversation without handing it over.',
        points: [
          { term: 'Campaign → Team → Agent', detail: 'Each step filters the next, so you only pick from agents who actually work this campaign.' },
          { term: 'Co-agents', detail: 'They can follow and work the conversation; ownership stays with the assigned agent.' },
          { term: 'Removing', detail: 'The × beside a name drops that co-agent — the conversation itself is unaffected.' },
        ],
      }}
      footer={<GhostButton onClick={onClose}>Done</GhostButton>}
    >
      <div className="space-y-4">
        <Field label="Campaign">
          <SelectField
            icon={<Building2 className="h-4 w-4" />}
            value={campaignId}
            onChange={(v) => { setCampaignId(v); setTeamId(''); setSelected('') }}
            placeholder="Select campaign"
            options={campaignOptions}
            allowClear
          />
        </Field>
        <Field label="Team">
          <SelectField
            icon={<Users className="h-4 w-4" />}
            value={teamId}
            onChange={(v) => { setTeamId(v); setSelected('') }}
            disabled={!campaignId}
            placeholder={campaignId ? 'Any team' : 'Select a campaign first'}
            options={teamOptions}
            allowClear
          />
        </Field>
        <Field label="Agent">
          <div className="flex gap-2">
            <div className="flex-1">
              <SelectField
                icon={<UserCog className="h-4 w-4" />}
                value={selected}
                onChange={setSelected}
                disabled={!campaignId}
                placeholder={campaignId ? 'Select an agent' : 'Select a campaign first'}
                options={agentOptions}
              />
            </div>
            <PrimaryButton onClick={() => void add()} disabled={!selected || actions.busy}>Add</PrimaryButton>
          </div>
        </Field>

        <div className="border-t border-slate-100 pt-3">
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
            Current collaborators {coAgentIds.length > 0 && `(${coAgentIds.length})`}
          </h4>
          {coAgentIds.length === 0 ? (
            <p className="text-sm text-slate-400">No collaborators yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {coAgentIds.map((id) => (
                <li
                  key={id}
                  className={`flex items-center justify-between ${R} border border-slate-200 bg-white px-3 py-2 text-sm`}
                >
                  <span className="flex items-center gap-2 text-slate-800">
                    <UserCog className="h-4 w-4 text-[#3366FF]" />
                    {labelFor.get(id) ?? id}
                  </span>
                  <button
                    type="button"
                    onClick={() => void actions.removeCoAgent(id)}
                    disabled={actions.busy}
                    className={`flex h-7 w-7 items-center justify-center ${R} text-slate-400 hover:bg-red-50 hover:text-red-500`}
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ActionDrawer>
  )
}
